import { DataTable } from "@msuite/ui/table";
import type { OrderFormSchema } from "@msuite/utils/schemas";
import { useEffect } from "react";
import { useFormContext } from "react-hook-form";
import { loadCarriersTableColumns, withoutStockThresholds } from "@/components/stock";
import { useLoadCarriers } from "@/hooks/use-load-carriers";
import { columns } from "./table-config";

const tableColumns = [...withoutStockThresholds(loadCarriersTableColumns), ...columns];

/**
 * The empty load carriers an order asks for — "Leergut": pallets and boxes shipped as goods rather
 * than as the vehicle for goods.
 *
 * Every carrier the organisation owns is listed rather than a selection made through a modal, which
 * is where this differs from the articles tab. The catalogue is a handful of rows, so a quantity
 * field beside each is quicker than choosing first and typing second — and it matches the picking
 * round's own carrier page, which lists them all for the same reason.
 *
 * Ordering these claims the yard: `availableQuantity` already accounts for the carriers unfinished
 * rounds have promised, and `insertOrder` refuses an order that goes past it. What the picker then
 * loads is the *same* pallets rather than a second pile — the ordered quantity is a floor on the
 * round, so a site asking for eighteen boxes that needs twenty-four to travel gets twenty-four.
 */
export function OrderFormLoadCarriers() {
	/** Hooks */
	const { loadCarriers, isLoading, queryKey, refetch } = useLoadCarriers();
	const methods = useFormContext<OrderFormSchema>();

	/** Effects */
	// The form array mirrors the carrier list by position, because that is what lets each row
	// register `loadCarriers.<index>.quantity` without a field array. Synced once the list lands,
	// and only when the shape actually differs, so typing a quantity does not rebuild the array
	// under the input that is focused.
	useEffect(() => {
		if (loadCarriers.length === 0) return;
		const current = methods.getValues("loadCarriers") ?? [];
		const isSynced =
			current.length === loadCarriers.length &&
			current.every((line, index) => line.loadCarrierId === loadCarriers[index]?.id);
		if (isSynced) return;

		const quantities = new Map<string, number>();
		for (const line of current) quantities.set(line.loadCarrierId, line.quantity);
		methods.setValue(
			"loadCarriers",
			loadCarriers.map((loadCarrier) => ({
				loadCarrierId: loadCarrier.id,
				quantity: quantities.get(loadCarrier.id) ?? 0,
			})),
		);
	}, [loadCarriers]);

	/** Render */
	return (
		<DataTable
			data={loadCarriers}
			columns={tableColumns}
			isLoading={isLoading}
			refetch={refetch}
			queryKey={queryKey}
			menu={null}
			persistenceKey="data-order-load-carriers-table"
			initialPinningState={{
				start: ["name"],
				end: ["quantity", "hint"],
			}}
		/>
	);
}
